import Canvas from 'diagram-js/lib/core/Canvas.js';
import {
  isNumber,
  debounce,
  assign,
  bind
} from 'min-dash';
import {
  append as svgAppend,
  classes as svgClasses,
  attr as svgAttr,
  create as svgCreate,
} from 'tiny-svg';

export class CustomCanvas extends Canvas {
  _eventBus;
  _graphicsFactory;
  _elementRegistry;

  constructor(config, eventBus, graphicsFactory, elementRegistry) {

    super(config, eventBus, graphicsFactory, elementRegistry);

    this._eventBus = eventBus;
    this._elementRegistry = elementRegistry;
    this._graphicsFactory = graphicsFactory;

  }

  ensurePx(number) {
    return isNumber(number) ? number + 'px' : number;
  }

  createContainer(options) {
    options = assign({}, { width: '100%', height: '100%' }, options);

    const container = options.container || document.body;

    // create a <div> around the svg element with the respective size
    // this way we can always get the correct container size
    // (this is impossible for <svg> elements at the moment)
    const parent = document.createElement('div');
    parent.setAttribute('class', 'djs-container');

    assign(parent.style, {
      position: 'relative',
      overflow: 'auto',
      width: this.ensurePx(options.width),
      height: this.ensurePx(options.height)
    });

    container.appendChild(parent);

    return parent;
  }

  createGroup(parent, cls, childIndex) {
    const group = svgCreate('g');
    svgClasses(group).add(cls);

    const index = childIndex !== undefined ? childIndex : parent.childNodes.length - 1;

    parent.insertBefore(group, parent.childNodes[index]);

    return group;
  }

  _init(config) {
    const eventBus = this._eventBus;

    // Creates a <svg> element that is wrapped into a <div>.
    // This way we are always able to correctly figure out the size of the svg element
    // by querying the parent node.
    //
    // (It is not possible to get the size of a svg element cross browser @ 2014-04-01)
    //
    // <div class="djs-container" style="width: {desired-width}, height: {desired-height}">
    //   <svg width="100%" height="100%">
    //    ...
    //   </svg>
    // </div>

    // html container
    const container = super._container = this.createContainer(config);

    const svg = super._svg = svgCreate('svg');
    svgAttr(svg, { width: '100%', height: '100%' });

    svgAppend(container, svg);

    const index = undefined;
    const viewport = super._viewport = this.createGroup(svg, 'viewport', index );

    super._layers = {};

    // debounce canvas.viewbox.changed events
    // for smoother diagram interaction
    if (config.deferUpdate !== false) {
      super._viewboxChanged = debounce(bind(super._viewboxChanged, this), 300);
    }

    eventBus.on('diagram.init', function() {

      /**
       * An event indicating that the canvas is ready to be drawn on.
       *
       * @memberOf Canvas
       *
       * @event canvas.init
       *
       * @type {Object}
       * @property {SVGElement} svg the created svg element
       * @property {SVGElement} viewport the direct parent of diagram elements and shapes
       */
      eventBus.fire('canvas.init', {
        svg: svg,
        viewport: viewport
      });

    }, this);

    // reset viewbox on shape changes to
    // recompute the viewbox
    eventBus.on([
      'shape.added',
      'connection.added',
      'shape.removed',
      'connection.removed',
      'elements.changed'
    ], function() {
      delete this._cachedViewbox;
    }, this);

    eventBus.on('diagram.destroy', 500, super._destroy, this);
    eventBus.on('diagram.clear', 500, super._clear, this);
  }
}
